// EL JARTI INGLÉS — tienda de enseres con entrega dentro del juego.
//
// Estética deliberadamente calcada de Amazon (cabecera azul marino, buscador
// central, fondo gris, tarjetas blancas, buy-box a la derecha, botones amarillo
// y naranja). Va a pantalla completa: se anulan los paddings del JartinetLayout
// con márgenes negativos para que la cabecera toque los bordes.
//
// El flujo es el de Amazon: catálogo → ficha → cesta → tramitar pedido →
// confirmación. La única diferencia conceptual es la "dirección de entrega":
// aquí es TU PERSONAJE DE MINECRAFT, que se resuelve en el servidor con las
// vinculaciones existentes. Si no se resuelve, NO se deja pedir.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import type { BankAccount, JartiMcIdentity, JartiProduct, JartiShopConfig } from '@/lib/types';
import { getMyBankAccounts, jartiShop_createOrder, jartiShop_getIdentity } from '@/lib/actions';
import {
  AMZ, AMZ_FONT, AmzButton, AmzPillButton, CartProvider, JartiInglesLogo, Price, Stars, SubNav,
  fakeRating, formatEntrega, useCart,
} from '@/components/jartiingles/ui';
import {
  Search, ShoppingCart, MapPin, ChevronDown, Package, Loader2, CheckCircle2, AlertTriangle,
  Trash2, Lock, Coins, Box, ArrowLeft, Truck, ShieldCheck,
} from 'lucide-react';

type View = { name: 'catalogo' } | { name: 'producto'; id: string } | { name: 'cesta' } | { name: 'checkout' } | { name: 'ok'; code: string; readyAtMs: number };

export default function ElJartiInglesPage() {
  return (
    <CartProvider>
      <Tienda />
    </CartProvider>
  );
}

function Tienda() {
  const { user, userData } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();

  const [view, setView] = useState<View>({ name: 'catalogo' });
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('Todo');
  const [identity, setIdentity] = useState<JartiMcIdentity | null>(null);
  const [config, setConfig] = useState<JartiShopConfig>({});
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loadingId, setLoadingId] = useState(true);

  const productsQuery = useMemo(() => query(collection(db, 'jarti_products')), []);
  const { data: rawProducts, isLoading } = useCollection<JartiProduct>(productsQuery);

  const products = useMemo(
    () => (rawProducts || [])
      .map((p) => sanitizeFirestoreData(p) as JartiProduct)
      .filter((p) => p.active !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name)),
    [rawProducts],
  );

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const categorias = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => { if (p.category?.trim()) set.add(p.category.trim()); });
    return ['Todo', ...Array.from(set).sort()];
  }, [products]);

  const visibles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (categoria !== 'Todo' && (p.category?.trim() || '') !== categoria) return false;
      if (!q) return true;
      return `${p.name} ${p.description} ${p.category || ''}`.toLowerCase().includes(q);
    });
  }, [products, search, categoria]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await jartiShop_getIdentity();
      if (!alive) return;
      setIdentity(r?.identity || { found: false });
      setConfig(r?.config || {});
      setLoadingId(false);
    })();
    (async () => {
      const r = await getMyBankAccounts() as { accounts?: BankAccount[] };
      if (!alive) return;
      const list = (r?.accounts || []).filter((a: BankAccount) => (a.accountType || 'secundaria') === 'secundaria');
      setAccounts(list);
    })();
    return () => { alive = false; };
  }, []);

  const prepMinutes = config.preparationMinutes ?? 120;
  const entregaMs = Date.now() + prepMinutes * 60 * 1000;

  const cartDetallado = useMemo(
    () => cart.lines
      .map((l) => ({ line: l, product: byId.get(l.productId) }))
      .filter((x): x is { line: typeof x.line; product: JartiProduct } => !!x.product),
    [cart.lines, byId],
  );
  const subtotal = cartDetallado.reduce((a, x) => a + (Number(x.product.price) || 0) * x.line.qty, 0);

  const goTop = () => window.scrollTo({ top: 0, behavior: 'auto' });
  const go = (v: View) => { setView(v); goTop(); };

  return (
    <div
      className="-m-4 min-h-screen md:-m-6 lg:-m-8"
      style={{ background: AMZ.page, color: AMZ.text, fontFamily: AMZ_FONT }}
    >
      {/* ── Cabecera ── */}
      <header style={{ background: AMZ.header }} className="px-3 py-2">
        <div className="mx-auto flex max-w-[1500px] items-center gap-2 md:gap-4">
          <button onClick={() => go({ name: 'catalogo' })} className="shrink-0 rounded border border-transparent p-1 hover:border-white/60">
            <JartiInglesLogo />
          </button>

          <button
            onClick={() => go({ name: 'checkout' })}
            className="hidden shrink-0 items-center gap-1 rounded border border-transparent px-2 py-1 text-left text-white hover:border-white/60 md:flex"
          >
            <MapPin className="h-4 w-4" />
            <span className="leading-tight">
              <span className="block text-[11px] text-white/70">Entregar a</span>
              <span className="block text-[13px] font-bold">
                {loadingId ? '…' : identity?.found ? (identity.mcName || 'Tu personaje') : 'Sin vincular'}
              </span>
            </span>
          </button>

          <div className="flex min-w-0 flex-1 items-stretch overflow-hidden rounded-[6px]">
            <select
              value={categoria}
              onChange={(e) => { setCategoria(e.target.value); go({ name: 'catalogo' }); }}
              className="hidden shrink-0 border-r border-[#cdcdcd] bg-[#e6e6e6] px-2 text-[12px] text-[#0f1111] outline-none sm:block"
              style={{ maxWidth: 150 }}
            >
              {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); if (view.name !== 'catalogo') go({ name: 'catalogo' }); }}
              placeholder="Buscar enseres, cirios, faldones…"
              className="min-w-0 flex-1 bg-white px-3 py-2 text-[14px] text-[#0f1111] outline-none placeholder:text-[#767676]"
            />
            <button
              onClick={() => go({ name: 'catalogo' })}
              className="grid w-11 shrink-0 place-items-center"
              style={{ background: AMZ.orange }}
              aria-label="Buscar"
            >
              <Search className="h-5 w-5 text-[#0f1111]" />
            </button>
          </div>

          <Link
            to="/jartinet/eljartiingles/pedidos"
            className="hidden shrink-0 rounded border border-transparent px-2 py-1 text-white hover:border-white/60 md:block"
          >
            <span className="block text-[11px] leading-tight text-white/80">
              Hola, {userData?.nickname || user?.email?.split('@')[0] || 'cofrade'}
            </span>
            <span className="block text-[13px] font-bold leading-tight">Cuenta y pedidos</span>
          </Link>

          <button
            onClick={() => go({ name: 'cesta' })}
            className="relative flex shrink-0 items-end gap-1 rounded border border-transparent px-2 py-1 text-white hover:border-white/60"
          >
            <span className="relative">
              <ShoppingCart className="h-7 w-7" />
              <span
                className="absolute -top-1 left-1/2 -translate-x-1/2 text-[14px] font-bold"
                style={{ color: AMZ.orange }}
              >
                {cart.count}
              </span>
            </span>
            <span className="hidden text-[13px] font-bold lg:block">Cesta</span>
          </button>
        </div>
      </header>

      <SubNav>
        {categorias.slice(0, 9).map((c) => (
          <button
            key={c}
            onClick={() => { setCategoria(c); go({ name: 'catalogo' }); }}
            className={`whitespace-nowrap rounded border px-2 py-0.5 ${categoria === c ? 'border-white/70' : 'border-transparent hover:border-white/40'}`}
          >
            {c}
          </button>
        ))}
        <span className="ml-auto hidden md:inline">
          <Link to="/jartinet/eljartiingles/pedidos" className="whitespace-nowrap rounded border border-transparent px-2 py-0.5 hover:border-white/40">
            Mis pedidos
          </Link>
        </span>
      </SubNav>

      {/* Aviso del admin */}
      {config.announcement ? (
        <div className="border-b border-black/10 bg-[#fef8e7] px-4 py-2 text-center text-[13px]">{config.announcement}</div>
      ) : null}
      {config.open === false ? (
        <div className="border-b border-black/10 bg-[#fdecea] px-4 py-2 text-center text-[13px] font-bold text-[#b12704]">
          La tienda está cerrada temporalmente. No se admiten pedidos nuevos.
        </div>
      ) : null}

      {/* ── Contenido ── */}
      <main className="mx-auto max-w-[1500px] px-3 py-4">
        {isLoading ? (
          <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#565959]" /></div>
        ) : view.name === 'catalogo' ? (
          <Catalogo
            products={visibles}
            todos={products}
            search={search}
            entregaMs={entregaMs}
            onOpen={(id) => go({ name: 'producto', id })}
            onAdd={(id) => cart.add(id, 1)}
          />
        ) : view.name === 'producto' ? (
          <FichaProducto
            product={byId.get(view.id)}
            entregaMs={entregaMs}
            identity={identity}
            onBack={() => go({ name: 'catalogo' })}
            onAdd={(qty) => { cart.add(view.id, qty); go({ name: 'cesta' }); }}
            onBuyNow={(qty) => { cart.add(view.id, qty); go({ name: 'checkout' }); }}
          />
        ) : view.name === 'cesta' ? (
          <Cesta
            lines={cartDetallado}
            subtotal={subtotal}
            entregaMs={entregaMs}
            onQty={(id, q) => cart.setQty(id, q)}
            onRemove={(id) => cart.remove(id)}
            onSeguir={() => go({ name: 'catalogo' })}
            onCheckout={() => go({ name: 'checkout' })}
          />
        ) : view.name === 'checkout' ? (
          <Checkout
            lines={cartDetallado}
            subtotal={subtotal}
            entregaMs={entregaMs}
            identity={identity}
            loadingIdentity={loadingId}
            accounts={accounts}
            shopOpen={config.open !== false}
            onBack={() => go({ name: 'cesta' })}
            onDone={(code, readyAtMs) => { cart.clear(); go({ name: 'ok', code, readyAtMs }); }}
          />
        ) : (
          <Confirmado code={view.code} readyAtMs={view.readyAtMs} onSeguir={() => go({ name: 'catalogo' })} onPedidos={() => navigate('/jartinet/eljartiingles/pedidos')} />
        )}
      </main>

      <footer style={{ background: AMZ.subheader }} className="mt-8 px-4 py-6 text-center text-[12px] text-white/70">
        <JartiInglesLogo small />
        <p className="mt-2">Los artículos se entregan dentro del servidor con <span className="font-mono text-white">/eljartiingles paquete</span>.</p>
        <p className="mt-1">El Jarti Inglés es una tienda ficticia de Jartiland. Se paga con oro del juego.</p>
      </footer>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Catálogo
// ════════════════════════════════════════════════════════════════════════════
function Catalogo({ products, todos, search, entregaMs, onOpen, onAdd }: {
  products: JartiProduct[];
  todos: JartiProduct[];
  search: string;
  entregaMs: number;
  onOpen: (id: string) => void;
  onAdd: (id: string) => void;
}) {
  const destacados = todos.filter((p) => p.featured).slice(0, 6);

  return (
    <div className="space-y-4">
      {/* Banner */}
      <div
        className="relative overflow-hidden rounded-[4px] px-6 py-8 md:px-10 md:py-12"
        style={{ background: 'linear-gradient(100deg,#232f3e 0%,#37475a 55%,#4a5c72 100%)' }}
      >
        <div className="relative z-10 max-w-2xl">
          <p className="text-[13px] font-bold uppercase tracking-widest" style={{ color: AMZ.orange }}>Todo para tu hermandad</p>
          <h1 className="mt-1 text-3xl font-bold leading-tight text-white md:text-4xl">
            Enseres, cirios y priostía<br />entregados en tu inventario
          </h1>
          <p className="mt-3 max-w-lg text-[14px] text-white/80">
            Pídelo aquí y recógelo en el servidor. Preparación estimada: <strong className="text-white">{formatEntrega(entregaMs)}</strong>.
          </p>
        </div>
        <Box className="absolute -right-6 -top-6 h-56 w-56 text-white/5" strokeWidth={0.6} />
      </div>

      {/* Franja de garantías, muy Amazon */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { icon: Truck, t: 'Entrega en el juego', d: 'Recoge el paquete con un comando' },
          { icon: ShieldCheck, t: 'Pago seguro', d: 'Cobro atómico desde JartiBank' },
          { icon: Coins, t: 'Cancelación gratuita', d: 'Mientras se prepara, te devolvemos el oro' },
        ].map(({ icon: Icon, t, d }) => (
          <div key={t} className="flex items-center gap-3 rounded-[4px] bg-white px-4 py-3">
            <Icon className="h-6 w-6 shrink-0" style={{ color: AMZ.link }} />
            <span>
              <span className="block text-[13px] font-bold">{t}</span>
              <span className="block text-[12px]" style={{ color: AMZ.muted }}>{d}</span>
            </span>
          </div>
        ))}
      </div>

      {destacados.length > 0 && !search && (
        <section className="rounded-[4px] bg-white p-4">
          <h2 className="mb-3 text-[21px] font-bold">Destacados de la semana</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {destacados.map((p) => (
              <button key={p.id} onClick={() => onOpen(p.id)} className="group text-left">
                <div className="grid aspect-square place-items-center overflow-hidden bg-white">
                  <Foto product={p} className="max-h-full max-w-full object-contain transition-transform group-hover:scale-105" />
                </div>
                <p className="mt-1 line-clamp-2 text-[13px] group-hover:underline" style={{ color: AMZ.link }}>{p.name}</p>
                <Price value={p.price} size="sm" />
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-[19px] font-bold">
            {search ? `Resultados para "${search}"` : 'Todos los artículos'}
          </h2>
          <span className="text-[13px]" style={{ color: AMZ.muted }}>{products.length} artículos</span>
        </div>

        {products.length === 0 ? (
          <div className="rounded-[4px] bg-white p-10 text-center">
            <Package className="mx-auto mb-3 h-10 w-10" style={{ color: AMZ.muted }} />
            <p className="text-[15px] font-bold">No hay artículos que mostrar</p>
            <p className="text-[13px]" style={{ color: AMZ.muted }}>Prueba con otra búsqueda o categoría.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p) => (
              <TarjetaProducto key={p.id} p={p} entregaMs={entregaMs} onOpen={() => onOpen(p.id)} onAdd={() => onAdd(p.id)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Foto({ product, className = '' }: { product: JartiProduct; className?: string }) {
  if (product.imageUrl) {
    return <img src={product.imageUrl} alt={product.name} className={className} loading="lazy" />;
  }
  return (
    <span className="grid h-full w-full place-items-center bg-[#f7f8f8]">
      <Package className="h-10 w-10" style={{ color: '#c7c9c9' }} />
    </span>
  );
}

function TarjetaProducto({ p, entregaMs, onOpen, onAdd }: { p: JartiProduct; entregaMs: number; onOpen: () => void; onAdd: () => void }) {
  const { stars, reviews } = fakeRating(p.id);
  const agotado = typeof p.stock === 'number' && p.stock === 0;

  return (
    <div className="flex flex-col rounded-[4px] bg-white p-4 transition-shadow hover:shadow-[0_2px_12px_rgba(0,0,0,.12)]">
      <button onClick={onOpen} className="grid h-44 place-items-center overflow-hidden">
        <Foto product={p} className="max-h-44 max-w-full object-contain" />
      </button>
      <button onClick={onOpen} className="mt-2 line-clamp-2 text-left text-[15px] leading-tight hover:underline" style={{ color: AMZ.link }}>
        {p.name}
      </button>
      <div className="mt-1"><Stars value={stars} count={reviews} /></div>
      <div className="mt-1"><Price value={p.price} size="md" /></div>
      <p className="mt-1 text-[12px]" style={{ color: AMZ.muted }}>
        Recíbelo <strong style={{ color: AMZ.text }}>{formatEntrega(entregaMs)}</strong>
      </p>
      {agotado ? (
        <p className="mt-1 text-[13px] font-bold" style={{ color: AMZ.deal }}>No disponible por ahora</p>
      ) : (
        <p className="mt-1 text-[13px]" style={{ color: AMZ.stock }}>En stock</p>
      )}
      <div className="mt-auto pt-3">
        <AmzPillButton block disabled={agotado} onClick={onAdd}>Añadir a la cesta</AmzPillButton>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Ficha de producto
// ════════════════════════════════════════════════════════════════════════════
function FichaProducto({ product, entregaMs, identity, onBack, onAdd, onBuyNow }: {
  product?: JartiProduct;
  entregaMs: number;
  identity: JartiMcIdentity | null;
  onBack: () => void;
  onAdd: (qty: number) => void;
  onBuyNow: (qty: number) => void;
}) {
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);

  if (!product) {
    return (
      <div className="rounded-[4px] bg-white p-10 text-center">
        <p className="text-[15px] font-bold">Este artículo ya no está disponible.</p>
        <AmzButton className="mt-4" onClick={onBack}>Volver a la tienda</AmzButton>
      </div>
    );
  }

  const { stars, reviews } = fakeRating(product.id);
  const galeria = [product.imageUrl, ...(product.images || [])].filter(Boolean) as string[];
  const agotado = typeof product.stock === 'number' && product.stock === 0;
  const maxQty = Math.min(
    product.maxPerOrder && product.maxPerOrder > 0 ? product.maxPerOrder : 30,
    typeof product.stock === 'number' && product.stock >= 0 ? product.stock : 30,
  ) || 1;

  return (
    <div>
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[13px] hover:underline" style={{ color: AMZ.link }}>
        <ArrowLeft className="h-4 w-4" /> Volver a los resultados
      </button>

      <div className="grid grid-cols-1 gap-6 rounded-[4px] bg-white p-5 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)_320px]">
        {/* Galería */}
        <div>
          <div className="grid aspect-square place-items-center overflow-hidden">
            {galeria.length > 0
              ? <img src={galeria[Math.min(img, galeria.length - 1)]} alt={product.name} className="max-h-full max-w-full object-contain" />
              : <Foto product={product} className="h-full w-full" />}
          </div>
          {galeria.length > 1 && (
            <div className="mt-3 flex gap-2">
              {galeria.map((g, i) => (
                <button
                  key={i}
                  onMouseEnter={() => setImg(i)}
                  onClick={() => setImg(i)}
                  className="h-14 w-14 overflow-hidden rounded-[4px] border"
                  style={{ borderColor: i === img ? AMZ.link : AMZ.border, borderWidth: i === img ? 2 : 1 }}
                >
                  <img src={g} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Información */}
        <div className="min-w-0">
          <h1 className="text-[24px] font-normal leading-tight">{product.name}</h1>
          <div className="mt-1 flex items-center gap-2">
            <Stars value={stars} count={reviews} />
            <span className="text-[13px]" style={{ color: AMZ.muted }}>· {reviews} valoraciones</span>
          </div>
          <hr className="my-3" style={{ borderColor: AMZ.border }} />
          <Price value={product.price} size="lg" />
          <p className="mt-1 text-[13px]" style={{ color: AMZ.muted }}>Precio final. Se paga con oro de tu cuenta de JartiBank.</p>

          {product.description && (
            <>
              <hr className="my-4" style={{ borderColor: AMZ.border }} />
              <h2 className="mb-2 text-[16px] font-bold">Sobre este artículo</h2>
              <p className="whitespace-pre-wrap text-[14px] leading-relaxed">{product.description}</p>
            </>
          )}

          {(product.items || []).length > 0 && (
            <>
              <hr className="my-4" style={{ borderColor: AMZ.border }} />
              <h2 className="mb-2 text-[16px] font-bold">Qué recibes en el juego</h2>
              <ul className="space-y-1">
                {(product.items || []).map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-[14px]">
                    <Box className="h-4 w-4" style={{ color: AMZ.muted }} />
                    <span className="font-bold">{it.count}×</span>
                    <span className="font-mono text-[12px]" style={{ color: AMZ.muted }}>{it.id}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Buy box */}
        <div className="h-fit rounded-[8px] border p-4" style={{ borderColor: AMZ.border }}>
          <Price value={product.price} size="lg" />
          <p className="mt-2 text-[14px]">
            Recíbelo <strong>{formatEntrega(entregaMs)}</strong>
          </p>
          <p className="mt-1 flex items-center gap-1 text-[13px]" style={{ color: AMZ.muted }}>
            <MapPin className="h-3.5 w-3.5" />
            {identity?.found ? <>Entregar a <strong style={{ color: AMZ.text }}>{identity.mcName || 'tu personaje'}</strong></> : 'Personaje sin vincular'}
          </p>
          <p className="mt-3 text-[18px]" style={{ color: agotado ? AMZ.deal : AMZ.stock }}>
            {agotado ? 'No disponible' : 'En stock'}
          </p>
          {typeof product.stock === 'number' && product.stock > 0 && product.stock <= 10 && (
            <p className="text-[13px]" style={{ color: AMZ.deal }}>Solo quedan {product.stock} — ¡pide ya!</p>
          )}

          <div className="mt-3 flex items-center gap-2">
            <label className="text-[13px]">Cantidad:</label>
            <div className="relative">
              <select
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                disabled={agotado}
                className="appearance-none rounded-[8px] border bg-[#f0f2f2] py-1 pl-3 pr-7 text-[13px]"
                style={{ borderColor: AMZ.border }}
              >
                {Array.from({ length: Math.max(1, maxQty) }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-4 w-4 -translate-y-1/2" />
            </div>
          </div>

          <div className="mt-3 space-y-2">
            <AmzPillButton block variant="yellow" disabled={agotado} onClick={() => onAdd(qty)}>Añadir a la cesta</AmzPillButton>
            <AmzPillButton block variant="orange" disabled={agotado} onClick={() => onBuyNow(qty)}>Comprar ya</AmzPillButton>
          </div>

          <p className="mt-3 flex items-start gap-1.5 text-[12px]" style={{ color: AMZ.muted }}>
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Transacción segura a través de JartiBank
          </p>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Cesta
// ════════════════════════════════════════════════════════════════════════════
type LineaDetalle = { line: { productId: string; qty: number }; product: JartiProduct };

function Cesta({ lines, subtotal, entregaMs, onQty, onRemove, onSeguir, onCheckout }: {
  lines: LineaDetalle[];
  subtotal: number;
  entregaMs: number;
  onQty: (id: string, q: number) => void;
  onRemove: (id: string) => void;
  onSeguir: () => void;
  onCheckout: () => void;
}) {
  const totalUds = lines.reduce((a, l) => a + l.line.qty, 0);

  if (lines.length === 0) {
    return (
      <div className="rounded-[4px] bg-white p-10 text-center">
        <ShoppingCart className="mx-auto mb-3 h-12 w-12" style={{ color: '#c7c9c9' }} />
        <h1 className="text-[21px] font-bold">Tu cesta de El Jarti Inglés está vacía</h1>
        <p className="mt-1 text-[14px]" style={{ color: AMZ.muted }}>Añade enseres desde el catálogo.</p>
        <AmzPillButton className="mt-4" onClick={onSeguir}>Seguir comprando</AmzPillButton>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="rounded-[4px] bg-white p-5">
        <h1 className="text-[28px] font-normal">Cesta de la compra</h1>
        <div className="mt-1 text-right text-[13px]" style={{ color: AMZ.muted }}>Precio</div>
        <hr style={{ borderColor: AMZ.border }} />

        {lines.map(({ line, product }) => (
          <div key={product.id} className="flex gap-4 border-b py-4" style={{ borderColor: AMZ.border }}>
            <div className="grid h-32 w-32 shrink-0 place-items-center">
              <Foto product={product} className="max-h-32 max-w-full object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[17px] leading-tight">{product.name}</p>
              <p className="mt-1 text-[13px]" style={{ color: AMZ.stock }}>En stock</p>
              <p className="mt-0.5 text-[12px]" style={{ color: AMZ.muted }}>
                Preparado {formatEntrega(entregaMs)}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <div className="relative">
                  <select
                    value={line.qty}
                    onChange={(e) => onQty(product.id, Number(e.target.value))}
                    className="appearance-none rounded-[8px] border bg-[#f0f2f2] py-1 pl-3 pr-7 text-[13px] shadow-sm"
                    style={{ borderColor: AMZ.border }}
                  >
                    {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-4 w-4 -translate-y-1/2" />
                </div>
                <span style={{ color: AMZ.border }}>|</span>
                <button onClick={() => onRemove(product.id)} className="inline-flex items-center gap-1 text-[13px] hover:underline" style={{ color: AMZ.link }}>
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <Price value={(Number(product.price) || 0) * line.qty} size="md" />
            </div>
          </div>
        ))}

        <div className="pt-3 text-right text-[18px]">
          Subtotal ({totalUds} {totalUds === 1 ? 'producto' : 'productos'}): <strong><Price value={subtotal} size="md" /></strong>
        </div>
      </div>

      <div className="h-fit rounded-[4px] bg-white p-4">
        <p className="text-[18px]">
          Subtotal ({totalUds} {totalUds === 1 ? 'producto' : 'productos'}): <strong><Price value={subtotal} size="md" /></strong>
        </p>
        <AmzPillButton block className="mt-3" onClick={onCheckout}>Tramitar pedido</AmzPillButton>
        <AmzPillButton block variant="ghost" className="mt-2" onClick={onSeguir}>Seguir comprando</AmzPillButton>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Checkout
// ════════════════════════════════════════════════════════════════════════════
function Checkout({ lines, subtotal, entregaMs, identity, loadingIdentity, accounts, shopOpen, onBack, onDone }: {
  lines: LineaDetalle[];
  subtotal: number;
  entregaMs: number;
  identity: JartiMcIdentity | null;
  loadingIdentity: boolean;
  accounts: BankAccount[];
  shopOpen: boolean;
  onBack: () => void;
  onDone: (code: string, readyAtMs: number) => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId && accounts.length > 0) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  const cuenta = accounts.find((a) => a.id === accountId);
  const saldo = cuenta ? (typeof cuenta.oro === 'number' ? cuenta.oro : 0) : 0;
  const sinSaldo = !!cuenta && saldo < subtotal;

  const puedePedir = !!identity?.found && !!cuenta && !sinSaldo && lines.length > 0 && shopOpen && !enviando;

  const pedir = async () => {
    if (!puedePedir) return;
    setEnviando(true);
    setError('');
    const r = await jartiShop_createOrder(accountId, lines.map((l) => ({ productId: l.product.id, qty: l.line.qty })));
    setEnviando(false);
    if (r?.success) {
      onDone(r.code, r.readyAtMs);
    } else {
      setError(String(r?.message || 'No se pudo completar el pedido.').replace('NO_MC_ACCOUNT: ', ''));
    }
  };

  if (lines.length === 0) {
    return (
      <div className="rounded-[4px] bg-white p-10 text-center">
        <p className="text-[16px] font-bold">No hay nada que tramitar.</p>
        <AmzPillButton className="mt-4" onClick={onBack}>Volver a la cesta</AmzPillButton>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 border-b pb-3 text-center" style={{ borderColor: AMZ.border }}>
        <h1 className="text-[28px] font-normal">Tramitar pedido</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {/* 1 — Entrega */}
          <section className="rounded-[4px] bg-white p-5">
            <h2 className="text-[18px] font-bold">1. Entrega en el juego</h2>
            <hr className="my-3" style={{ borderColor: AMZ.border }} />
            {loadingIdentity ? (
              <p className="flex items-center gap-2 text-[14px]" style={{ color: AMZ.muted }}>
                <Loader2 className="h-4 w-4 animate-spin" /> Comprobando tu personaje…
              </p>
            ) : identity?.found ? (
              <div className="flex items-center gap-3">
                {identity.mcUuid && (
                  <img
                    src={`https://mc-heads.net/avatar/${identity.mcUuid}/56`}
                    alt=""
                    className="h-14 w-14 rounded-[4px]"
                    style={{ imageRendering: 'pixelated' }}
                  />
                )}
                <div>
                  <p className="text-[15px] font-bold">{identity.mcName || 'Tu personaje'}</p>
                  <p className="text-[13px]" style={{ color: AMZ.muted }}>
                    Servidor principal de Jartiland · recogida con <span className="font-mono">/eljartiingles paquete</span>
                  </p>
                  <p className="mt-0.5 text-[12px]" style={{ color: AMZ.muted }}>
                    Cuenta identificada por {identity.source === 'bank' ? 'tu vinculación de JartiBank' : 'la vinculación del administrador'}.
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-[4px] border p-4" style={{ borderColor: '#e77600', background: '#fff8f0' }}>
                <p className="flex items-center gap-2 text-[15px] font-bold" style={{ color: '#b12704' }}>
                  <AlertTriangle className="h-5 w-5" /> No podemos identificar tu personaje de Minecraft
                </p>
                <p className="mt-2 text-[14px]">
                  Para que el paquete llegue a alguien, tu cuenta de Jartinet tiene que estar vinculada a tu personaje.
                  Puedes hacerlo tú desde el juego creando una cuenta principal de JartiBank, o pedirle a un
                  administrador que te vincule.
                </p>
                <p className="mt-2 text-[13px]" style={{ color: AMZ.muted }}>
                  Mientras tanto no se pueden tramitar pedidos.
                </p>
              </div>
            )}
          </section>

          {/* 2 — Pago */}
          <section className="rounded-[4px] bg-white p-5">
            <h2 className="text-[18px] font-bold">2. Forma de pago</h2>
            <hr className="my-3" style={{ borderColor: AMZ.border }} />
            {accounts.length === 0 ? (
              <p className="text-[14px]" style={{ color: '#b12704' }}>
                No tienes ninguna cuenta secundaria de JartiBank. Crea una desde JartiBank para poder pagar.
              </p>
            ) : (
              <div className="space-y-2">
                {accounts.map((a) => (
                  <label key={a.id} className="flex cursor-pointer items-center gap-3 rounded-[4px] border p-3" style={{ borderColor: accountId === a.id ? AMZ.link : AMZ.border }}>
                    <input type="radio" name="cuenta" checked={accountId === a.id} onChange={() => setAccountId(a.id)} />
                    <Coins className="h-5 w-5" style={{ color: AMZ.orange }} />
                    <span className="flex-1">
                      <span className="block text-[14px] font-bold">{a.accountName}</span>
                      <span className="block text-[12px] font-mono" style={{ color: AMZ.muted }}>{a.iban}</span>
                    </span>
                    <span className="text-right">
                      <span className="block text-[12px]" style={{ color: AMZ.muted }}>Saldo</span>
                      <Price value={typeof a.oro === 'number' ? a.oro : 0} size="sm" />
                    </span>
                  </label>
                ))}
                {sinSaldo && (
                  <p className="text-[13px] font-bold" style={{ color: '#b12704' }}>
                    Saldo insuficiente en esta cuenta para el total del pedido.
                  </p>
                )}
              </div>
            )}
          </section>

          {/* 3 — Revisar */}
          <section className="rounded-[4px] bg-white p-5">
            <h2 className="text-[18px] font-bold">3. Revisa los artículos</h2>
            <hr className="my-3" style={{ borderColor: AMZ.border }} />
            <p className="mb-3 text-[15px] font-bold" style={{ color: AMZ.stock }}>
              Preparación estimada: {formatEntrega(entregaMs)}
            </p>
            <div className="space-y-3">
              {lines.map(({ line, product }) => (
                <div key={product.id} className="flex items-center gap-3">
                  <div className="grid h-16 w-16 shrink-0 place-items-center">
                    <Foto product={product} className="max-h-16 max-w-full object-contain" />
                  </div>
                  <span className="min-w-0 flex-1 text-[14px]">{product.name}</span>
                  <span className="text-[13px]" style={{ color: AMZ.muted }}>x{line.qty}</span>
                  <Price value={(Number(product.price) || 0) * line.qty} size="sm" />
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Resumen */}
        <div className="h-fit space-y-3 rounded-[4px] bg-white p-4 lg:sticky lg:top-4">
          <AmzPillButton block disabled={!puedePedir} onClick={pedir}>
            {enviando ? 'Realizando pedido…' : 'Realizar el pedido'}
          </AmzPillButton>
          <p className="text-[12px]" style={{ color: AMZ.muted }}>
            Al hacer el pedido aceptas que el importe se descuente de tu cuenta de JartiBank.
          </p>
          {error && (
            <p className="rounded-[4px] border p-2 text-[13px]" style={{ borderColor: '#b12704', color: '#b12704', background: '#fdecea' }}>
              {error}
            </p>
          )}
          <hr style={{ borderColor: AMZ.border }} />
          <h3 className="text-[17px] font-bold">Resumen del pedido</h3>
          <div className="flex justify-between text-[14px]">
            <span>Productos:</span><Price value={subtotal} size="sm" />
          </div>
          <div className="flex justify-between text-[14px]">
            <span>Envío:</span><span style={{ color: AMZ.stock }}>GRATIS</span>
          </div>
          <hr style={{ borderColor: AMZ.border }} />
          <div className="flex items-center justify-between text-[18px] font-bold" style={{ color: '#b12704' }}>
            <span>Total del pedido:</span><Price value={subtotal} size="md" />
          </div>
          <button onClick={onBack} className="text-[13px] hover:underline" style={{ color: AMZ.link }}>Volver a la cesta</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Confirmación
// ════════════════════════════════════════════════════════════════════════════
function Confirmado({ code, readyAtMs, onSeguir, onPedidos }: { code: string; readyAtMs: number; onSeguir: () => void; onPedidos: () => void }) {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="rounded-[4px] bg-white p-8">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="h-10 w-10 shrink-0" style={{ color: AMZ.stock }} />
          <div>
            <h1 className="text-[28px] font-normal" style={{ color: AMZ.stock }}>¡Pedido realizado!</h1>
            <p className="mt-1 text-[15px]">
              Tu pedido <strong>{code}</strong> ya se está preparando.
            </p>
            <p className="mt-3 text-[15px]">
              Estará listo <strong>{formatEntrega(readyAtMs)}</strong>. Te avisaremos con una carta al buzón.
            </p>
            <div className="mt-4 rounded-[4px] border p-4" style={{ borderColor: AMZ.border, background: '#f7fafa' }}>
              <p className="text-[14px] font-bold">Cómo recogerlo</p>
              <ol className="mt-2 list-inside list-decimal space-y-1 text-[14px]">
                <li>Entra al servidor principal de Jartiland.</li>
                <li>Asegúrate de tener huecos libres en el inventario.</li>
                <li>Escribe <span className="rounded bg-[#232f3e] px-1.5 py-0.5 font-mono text-[13px] text-white">/eljartiingles paquete</span></li>
              </ol>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <AmzPillButton onClick={onPedidos}>Ver mis pedidos</AmzPillButton>
              <AmzPillButton variant="ghost" onClick={onSeguir}>Seguir comprando</AmzPillButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
